import { useNavigate } from "react-router-dom";
import { Plus, KeyRound } from "lucide-react";
import { useLeagues } from "@/modules/leagues/hooks/useLeagues";
import { LeagueCard } from "@/modules/leagues/components/LeagueCard";
import { Button } from "@/components/atoms/Button";
import { SkeletonCard } from "@/components/atoms/Skeleton";
import { ErrorState } from "@/components/organisms/ErrorState";
import { EmptyState } from "@/components/organisms/EmptyState";
import { TrophyIllustration } from "@/components/atoms/illustrations";
import { ROUTES } from "@/constants/routes";

/**
 * Lista completa de Minhas Ligas (Priority 9 do escopo "bolão funcional de
 * verdade") — ligas reais do Firestore que o usuário criou ou participa,
 * nunca dados fictícios. Substitui o antigo placeholder "Em breve".
 */
export function MyLeaguesPage() {
  const navigate = useNavigate();
  const { data: leagues, isLoading, isError, refetch } = useLeagues();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-lg font-bold">Minhas ligas</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(ROUTES.joinLeague)}>
            <KeyRound className="h-4 w-4" /> Tenho um código
          </Button>
          <Button size="sm" onClick={() => navigate(ROUTES.createLeague)}>
            <Plus className="h-4 w-4" /> Criar liga
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {isError && (
        <ErrorState
          title="Não foi possível carregar suas ligas"
          description="Verifique sua conexão e tente novamente."
          onRetry={() => refetch()}
        />
      )}

      {leagues && leagues.length === 0 && (
        <EmptyState
          illustration={TrophyIllustration}
          title="Você ainda não participa de nenhuma liga"
          description="Crie um bolão para a família, os amigos ou o trabalho, ou entre com um código de convite."
          actionLabel="Criar minha primeira liga"
          onAction={() => navigate(ROUTES.createLeague)}
          secondaryActionLabel="Tenho um código"
          onSecondaryAction={() => navigate(ROUTES.joinLeague)}
        />
      )}

      {leagues && leagues.length > 0 && (
        <div className="space-y-3">
          {leagues.map((league) => (
            <LeagueCard key={league.id} league={league} onClick={() => navigate(ROUTES.leaguePredictions(league.id))} />
          ))}
        </div>
      )}
    </div>
  );
}
